// Moving between pages without a page load: following the site's own links
// and the browser's back and forward, and where back links lead. Which page
// a URL draws is app.ts's route, handed in with onRoute.

import { app, eventTarget } from "./dom.ts";
import { announceTitle } from "./head.ts";
import { backLinkHtml } from "./markup.ts";
import { escapeHtml } from "./web.ts";

// Draws the page for the current URL: app.ts's route, once it's set.
let route = (): void => {};

export const onRoute = (draw: () => void): void => {
  route = draw;
};

// Opens `href` (relative to this page, or whole). `scroll: false` stays where
// the reader is, for a page that only changed its query (the search page's
// form). Otherwise it's a new page: focus moves to it, as a page load would,
// and screen readers hear its title. Each entry remembers the page it was
// left from, for back links (see below).
export function navigate(href: string, { scroll = true }: { scroll?: boolean } = {}): void {
  const url = new URL(href, location.href).href;
  if (url !== location.href) {
    history.pushState({ from: location.href }, "", url);
  }

  route();
  if (scroll) {
    window.scrollTo(0, 0);
    app.focus({ preventScroll: true });
    announceTitle();
  }
}

window.addEventListener("popstate", () => route());

// Whether the screen has no hover: a phone or tablet.
const touchOnly = window.matchMedia("(hover: none)");

// The badge a first tap showed the tip of (see below), until the next click.
let tappedBadge: HTMLAnchorElement | null = null;

document.addEventListener("click", (event) => {
  const link = eventTarget<HTMLAnchorElement>(event, "a[data-nav]");
  const tapped = tappedBadge;
  tappedBadge = null;

  if (
    !link ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return;
  }

  event.preventDefault();

  // A back link to the page just before this one goes back in history, so
  // the browser puts the reader where they were on it.
  if (link.matches(".back") && (history.state as { from?: string } | null)?.from === link.href) {
    history.back();
    return;
  }

  // On a touch screen there's no hover to show a badge's tip (rows.ts's
  // moverBadges), so a first tap focuses the badge, which shows it, and a
  // second tap on it follows the link. (A tap focuses a link before its
  // click, so focus can't say which tap this is.)
  if (link.matches(".badge") && touchOnly.matches && tapped !== link) {
    tappedBadge = link;
    link.focus();
    return;
  }

  navigate(link.href);
});

// The list page last shown, so "All games" returns to the same page.
let listHref = "/";

export const backLink = (): string => backLinkHtml(listHref);

// Where a game page's back link goes: the last list of games shown (the
// ranking, a search, a tag's or a company's page, the tags index), so it
// leads back the way the reader came. Game pages linked from a game page
// keep it.
let backTo = { href: "/", label: "All games" };

export const listBackLink = (): string => backLinkHtml(escapeHtml(backTo.href), escapeHtml(backTo.label));

// The list page `href` is now shown: "All games" leads back to it, and so
// do game pages.
export function setListHref(href: string): void {
  listHref = href;
  backTo = { href, label: "All games" };
}

// Remembers the page now shown as where game pages lead back to.
export const setBackTo = (label: string): void => {
  backTo = { href: location.pathname + location.search, label };
};
